<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref } from "vue";
import type { ToastMessageType } from "~/types/Toast";
import ExclamationTriangleIcon from "@primevue/icons/exclamationtriangle";
import InfoCircleIcon from "@primevue/icons/infocircle";
import TimesCircleIcon from "@primevue/icons/timescircle";
import CheckIcon from "@primevue/icons/check";
import { ToastStyle } from "./toastStyle";

const props = defineProps<{
  message: ToastMessageType;
  open: boolean;
}>();

const emit = defineEmits<{
  close: [message: ToastMessageType, type: "life-end" | "close"];
  remove: [message: ToastMessageType];
  "update:open": [value: boolean];
}>();

const messageClasses = computed(() =>
  ToastStyle.classes.message(props.message.severity),
);
const iconClasses = computed(() =>
  ToastStyle.classes.icon(props.message.severity),
);
const textClasses = computed(() => ToastStyle.classes.text());
const summaryClasses = computed(() => ToastStyle.classes.summary());
const detailClasses = computed(() => ToastStyle.classes.detail());
const closeButtonClasses = computed(() => ToastStyle.classes.closeButton());
const contentMessageClasses = computed(() => ToastStyle.classes.content());

const iconComponent = computed(() => {
  const icons = {
    info: InfoCircleIcon,
    success: CheckIcon,
    warn: ExclamationTriangleIcon,
    error: TimesCircleIcon,
  };
  return props.message.severity ? icons[props.message.severity] : null;
});

function onCloseClick() {
  emit("close", { message: props.message, type: "close" });
}

function onOpenChange(open: boolean) {
  emit("update:open", open);
  if (!open) {
    emit("remove", props.message);
  }
}
</script>

<template>
  <ToastRoot
    :duration="props.message?.life" :class="[messageClasses]"
    @update:open="onOpenChange">
    <div :class="contentMessageClasses">
      <component :is="iconComponent" :class="iconClasses" aria-hidden="true" />

      <div :class="textClasses">
        <ToastTitle :class="summaryClasses">
          {{ message.summary }}
        </ToastTitle>
        <ToastDescription v-if="message.detail" :class="detailClasses">
          {{ message.detail }}
        </ToastDescription>
      </div>
      <ToastClose :class="closeButtonClasses" @click="onCloseClick"/>
    </div>
  </ToastRoot>
</template>
